/**
 * Área FICHA · LEITURA ESTRATÉGICA (IA): a análise gravada, o consentimento da cliente e o pedido
 * de uma análise nova à Edge Function.
 *
 * A CHAVE DA IA NÃO PASSA POR AQUI: a chamada ao modelo e o segredo moram só na Edge Function
 * `analisar-cliente-ia` (`admin.html:6167` a `6170`). Este módulo só lê e pede.
 *
 * O CONSENTIMENTO É SÓ LIDO. A tabela `consentimento_analise_ia` aceita insert apenas de quem é a
 * própria cliente (`admin.html:6199` a `6202`), e este módulo não tem função de gravar nela de
 * propósito: se um dia aparecer uma, é defeito.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  comEscopo,
  executar,
  invocarFuncao,
  type Escopo,
  type FonteDeDados,
  type LinhaAnalisesIa,
  type LinhaConsentimentoAnaliseIa,
  type Resultado,
} from '../nucleo.js';

/**
 * A versão do texto de consentimento em vigor (`admin.html:6194`). Tem que ser igual à do portal
 * da cliente e à da Edge Function: é ela que decide se o "sim" ainda vale.
 */
export const VERSAO_CONSENTIMENTO_IA = '2026-08-14';

/**
 * Lê a análise gravada da ficha aberta.
 * Legado: `admin.html:6269` a `6272`, `comEscopo(sb.from('analises_ia').select('*')).maybeSingle()`.
 */
export function lerAnaliseIa(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<LinhaAnalisesIa | null>> {
  // Sem análise ainda, `dados` nulo e o botão diz "Criar análise".
  return executar<LinhaAnalisesIa | null>(
    comEscopo(clienteDe(fonte).from('analises_ia').select('*'), escopo).maybeSingle(),
  );
}

/** As duas colunas que o legado lê do consentimento. */
export type RespostaDeConsentimento = Pick<LinhaConsentimentoAnaliseIa, 'concedido' | 'registrado_em'>;

/**
 * Lê a resposta mais recente da cliente sobre a análise por IA, na versão de texto em vigor.
 * Legado: `admin.html:6214` a `6221`, `select('concedido, registrado_em').eq('user_id', userId)
 * .eq('versao_texto', VERSAO).order('registrado_em', desc).order('id', desc).limit(1)`.
 *
 * O FILTRO É POR CLIENTE, e não pelo lead: o consentimento é da pessoa, e vale para todo
 * atendimento dela, como no legado.
 */
export function lerConsentimentoIa(
  fonte: FonteDeDados,
  userId: string,
): Promise<Resultado<readonly RespostaDeConsentimento[] | null>> {
  return executar<readonly RespostaDeConsentimento[] | null>(
    clienteDe(fonte)
      .from('consentimento_analise_ia')
      .select('concedido, registrado_em')
      .eq('user_id', userId)
      // Aceite de versão antiga não conta (`admin.html:6192`).
      .eq('versao_texto', VERSAO_CONSENTIMENTO_IA)
      // A mais recente primeiro; o `id` desempata duas respostas no mesmo instante.
      .order('registrado_em', { ascending: false })
      .order('id', { ascending: false })
      .limit(1),
  );
}

/** O ponto de estilo raiz já calculado na tela, que o legado manda pronto (`admin.html:6315`). */
export interface PontoEstiloRaiz {
  readonly estilo: string;
  readonly pontuacao: number;
}

/** O corpo que o legado manda à Edge Function (`admin.html:6311` a `6317`). */
export interface PedidoDeAnalise {
  readonly user_id: string;
  readonly lead_id: number | null;
  readonly informacoes_complementares: string;
  readonly pontos_estilo_raiz: readonly PontoEstiloRaiz[];
  readonly abas_habilitadas: readonly string[];
}

/** O que a função devolve em 2xx: a análise, ou um `error` de regra de negócio (`6335`). */
export interface RespostaDaAnalise {
  readonly analise?: string;
  readonly error?: string;
}

/**
 * Pede uma análise nova à Edge Function.
 * Legado: `admin.html:6310`, `sb.functions.invoke('analisar-cliente-ia', { body })`.
 *
 * Status fora de 2xx volta em `erro`, com o corpo já lido (`erro.corpo.error` é o motivo que o
 * legado mostra, `6327` a `6331`). Um 200 com `{ error }` volta em `dados`, como no legado.
 */
export function pedirAnaliseIa(fonte: FonteDeDados, pedido: PedidoDeAnalise): Promise<Resultado<RespostaDaAnalise | null>> {
  // O corpo vai como veio da tela, com as cinco chaves do legado e nada a mais.
  return invocarFuncao<RespostaDaAnalise | null>(fonte, 'analisar-cliente-ia', {
    body: {
      user_id: pedido.user_id,
      lead_id: pedido.lead_id,
      informacoes_complementares: pedido.informacoes_complementares,
      pontos_estilo_raiz: pedido.pontos_estilo_raiz,
      abas_habilitadas: pedido.abas_habilitadas,
    },
  });
}
